import React from "react";
import * as Icons from "lucide-react";
import { ORG, OPTIONAL_INTEGRATIONS } from "@/lib/constants";
import { MIRRORS } from "@/lib/layers";
import { PageHeader, SectionCard, InfoNote } from "@/components/uikit";
import { SystemInfoPanel } from "@/components/SystemInfoPanel";

const PIPELINE = [
  { label: "Capture", icon: "FileInput", desc: "Ingest an event, document, AI response, telemetry object, or system state." },
  { label: "Canonicalize", icon: "Braces", desc: "Deterministic JSON with sorted keys for stable hashing." },
  { label: "Hash", icon: "Hash", desc: "SHA-256 digest via WebCrypto (K-Crypto)." },
  { label: "Anchor", icon: "Anchor", desc: "Append a chained record referencing the previous hash." },
  { label: "Detect Drift", icon: "Waves", desc: "Deterministic anomaly scoring across observations." },
  { label: "Verify", icon: "ShieldCheck", desc: "Recompute hashes to confirm integrity / detect tampering." },
  { label: "Reconstruct", icon: "History", desc: "Replay state history and locate any chain break." },
];

export default function Architecture() {
  return (
    <div>
      <PageHeader overline="System Architecture" title="Architecture Explorer" icon="Network"
        subtitle="How SignalLink AI turns raw events into an independent, locally verifiable evidence layer." />

      <SectionCard title="Operating Pipeline" icon="Workflow" className="mb-6" testId="architecture-pipeline">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PIPELINE.map((p, i) => {
            const Icon = Icons[p.icon];
            return (
              <div key={p.label} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md border border-cyan-800 bg-slate-950 text-cyan-400">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="font-mono text-[10px] text-slate-600">STEP {i + 1}</span>
                </div>
                <p className="font-heading text-sm font-bold uppercase tracking-wide text-slate-200">{p.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="ADA-4WM — Four Mirrors" icon="LayoutGrid" className="lg:col-span-2" testId="architecture-mirrors">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {MIRRORS.map((m) => (
              <div key={m.id} className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
                <p className="font-heading text-sm font-bold uppercase text-cyan-300">{m.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Layers {m.range[0]}–{m.range[1]}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-400">{m.summary}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        <SystemInfoPanel testId="architecture-sysinfo" showPrinciple={false} />
      </div>

      <SectionCard title="Optional Integrations" icon="Plug" className="mt-6" testId="architecture-integrations">
        <InfoNote tone="info" className="mb-3">
          The core console runs entirely locally. The adapters below are architecturally useful but{" "}
          <strong>not required</strong> and are intentionally disabled.
        </InfoNote>
        <div className="space-y-2">
          {OPTIONAL_INTEGRATIONS.map((it) => (
            <div key={it.name} className="flex items-center justify-between gap-3 rounded border border-slate-800 bg-slate-950/50 px-3 py-2">
              <span className="flex items-center gap-2 text-sm text-slate-300">
                <Icons.PlugZap className="h-4 w-4 text-slate-600" aria-hidden="true" /> {it.name}
              </span>
              <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">{it.status}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      <p className="mt-8 text-center font-heading text-sm italic tracking-wide text-slate-600" data-testid="architecture-tagline">
        “{ORG.tagline}”
      </p>
    </div>
  );
}
